import type { FC, MouseEvent } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

// Status tones shared with the Documents page checklist: a light background
// with a darker dot at left. Gray = nothing known yet, blue = in progress,
// green = complete.
export type StatusTone = "unknown" | "progress" | "complete";

export const STATUS_TONES: Record<StatusTone, { bg: string; dot: string }> = {
  unknown: { bg: "#f5f5f5", dot: "grey.500" },
  progress: { bg: "#e3f2fd", dot: "info.main" },
  complete: { bg: "#e8f5e9", dot: "success.main" },
};

interface StatusDotChipProps {
  label: string;
  tone: StatusTone;
  tooltip?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

export const StatusDotChip: FC<StatusDotChipProps> = ({
  label,
  tone,
  tooltip,
  onClick,
}) => {
  const colors = STATUS_TONES[tone];
  const chip = (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      icon={
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: colors.dot,
            ml: 0.5,
          }}
        />
      }
      sx={{
        bgcolor: colors.bg,
        color: "black",
        cursor: onClick ? "pointer" : "default",
        fontSize: { xs: "0.7rem", sm: "0.8125rem" },
        height: { xs: "20px", sm: "24px" },
        "&:hover": { bgcolor: colors.bg, opacity: onClick ? 0.8 : 1 },
        "& .MuiChip-icon": { marginLeft: "8px", marginRight: "-2px" },
        "& .MuiChip-label": { px: { xs: 1, sm: 1.5 } },
      }}
    />
  );
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};
